import { Injectable } from '@angular/core';
import { ProjectService } from '../project/project.service';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { first, filter } from 'rxjs/operators';
import { ApiService } from '../../api/api.service';
import { ProjectSubmodule } from './project-submodule.interface';
import { ModuleService } from '../module/module.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Submodule core service.
 *
 * Contains all the private logic for Submodule management.
 *
 * **Only SubmoduleService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class SubmoduleCoreService {

  /** Submodule subject. */
  protected _submodules: Subject<ProjectSubmodule[]> = new BehaviorSubject<ProjectSubmodule[]>(null);

  /** Submodule observable. */
  public submodule$ = this._submodules.asObservable();

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected moduleService: ModuleService,
    protected projectService: ProjectService
  ) {
    this._trackModules();
  }


  /**
   * Get submodules.
   *
   * @returns The current submodules.
   */
  public get(): ProjectSubmodule[] {
    let submodules: ProjectSubmodule[];
    this.submodule$.pipe(first()).subscribe(m => submodules = m);
    return submodules;
  }

  /**
   * Get one submodule.
   *
   * @param id The id of the requested submodule.
   * @returns The requested submodule.
   */
  public getSubmodule(id: ProjectSubmodule['_id']): ProjectSubmodule {
    return this.get().find(s => s._id === id);
  }

  /**
   * Delete submodule.
   *
   * When deleting a submodule, a few things are done:
   *  - Call API to request a delete on backend.
   *  - Patch the local copy of the submodule.
   *  - Mark any child element as `active: false`.
   *
   * @note Each submodule is patched individually. Should be the best solution because
   *  it handles the exact case of 'patch these N submodules', however it triggers the
   *  observable a lot of times, one time for the current submodules, and every time for
   *  each adjecent sibling submodules. When performance will be an issue, just patch the
   *  parent (module) once.
   *
   * @param submodule The submodule to delete.
   */
  protected _delete(submodule: ProjectSubmodule): Observable<boolean> {
    return new Observable(observer => {
      this.apiLegacyService.deleteSubmodule(submodule).subscribe(() => {
        const module = this.moduleService.getModule(submodule.moduleId);
        const index = module.submodules.findIndex(s => s._id === submodule._id);
        module.submodules.forEach((sub, i) => {
          if (i === index) {
            for (const e of submodule.elements) {
              e.active = false;
            }
            this.projectService.patchSubmodule(sub, { active: false });
          }
        });
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Patch submodule.
   *
   * @param submodule The submodule to patch.
   * @param values The updated values
   * @returns Observable with state.
   */
  protected _patchSubmodule(submodule: ProjectSubmodule, values: Partial<ProjectSubmodule>, config?: any): Observable<boolean> {
    return new Observable(observer => {
      this.apiLegacyService.patchSubmodule(submodule, values).subscribe(response => {
        this.projectService.patchSubmodule(submodule, values);
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Track modules and set submodules.
   */
  protected _trackModules() {
    this.moduleService.module$.pipe(
      filter(modules => modules !== null)
    ).subscribe(modules => {
      const submodules = [];
      modules.forEach(module => {
        submodules.push(...module.submodules);
      });
      this._submodules.next(submodules);
    });
  }
}
